import { createCallable } from 'react-call';
import { Heading } from '@altify/components';
import { useContent } from '@altify/utils';
import { content } from './AiInsights.content';
import { DialogOrBottomSheet } from 'components/DialogOrBottomSheet/DialogOrBottomSheet';
import { AiInsightsIcon } from 'assets/icons/AiInsightsIcon';
import { useState } from 'react';
import BackButton from 'components/BackButton/BackButton';
import { AiInsightsContent } from './AiInsightsContent';
import { AiInsightsQuestionsList } from './AiInsightsQuestionsList';
import { useGetAccessToSegmentQuery } from 'store/slices/profile-api';
import { useNavigate } from '@tanstack/react-router';

interface AiInsightsModalProps {
  pageName: 'Explore' | 'Portfolio' | 'Assets';
  question?: string;
  productType?: string;
  productCode?: string;
  isOpen: boolean;
  onClose: () => void;
}

export const AiInsightsModal = ({
  isOpen,
  pageName,
  question,
  productType,
  productCode,
  onClose,
}: AiInsightsModalProps) => {
  const navigate = useNavigate();
  const [showAiInsightsContent, setShowAiInsightsContent] = useState(
    question !== undefined,
  );
  const { data: hasAccessToBetaFeatures } =
    useGetAccessToSegmentQuery('BETA_AI_FEATURES');
  const [askedQuestion, setAskedQuestion] = useState(question ?? '');
  const localizedContent = useContent(content);
  return (
    <>
      {showAiInsightsContent ? (
        <>
          <div className="w-full">
            <BackButton
              className="w-full text-left lg:bg-body-background mt-1 pt-2"
              onClick={onClose}
            />
          </div>
          <div className="w-full pt-2">
            <AiInsightsContent
              pageName={pageName}
              question={askedQuestion}
              onClose={onClose}
              isFullPage={true}
              productCode={productCode}
              productType={productType}
              setAskedQuestion={setAskedQuestion}
            />
          </div>
        </>
      ) : (
        <DialogOrBottomSheet
          isOpen={isOpen}
          blurEnabled={true}
          onClose={onClose}
          backgroundColor="#0C1C2C">
          <div className="flex flex-col items-center gap-4 px-6 pb-12">
            <div className="items-center w-full gap-8">
              <div className="flex flex-row flex-grow justify-center items-center gap-2">
                <div className="bg-[#B393FF] rounded-full flex justify-center items-center h-8 w-8">
                  <div className="stroke-neutral-600">
                    <AiInsightsIcon />
                  </div>
                </div>
                <Heading level={4}>{localizedContent.title}</Heading>
              </div>
            </div>
            <AiInsightsQuestionsList
              pageName={pageName}
              hasAccessToBetaFeatures={hasAccessToBetaFeatures}
              productType={productType}
              productCode={productCode}
              onPress={question => {
                if (question === 'Start a new chat') {
                  navigate({ to: '/ai-insights' });
                  onClose();
                } else {
                  setAskedQuestion(question);
                  setShowAiInsightsContent(true);
                }
              }}
            />
          </div>
        </DialogOrBottomSheet>
      )}
    </>
  );
};

interface CallableProps {
  pageName: 'Explore' | 'Portfolio' | 'Assets';
  question?: string;
  productType?: string;
  productCode?: string;
}

export const AiInsightsModalCallable = createCallable<CallableProps, boolean>(
  ({ call, pageName, question, productType, productCode }) => (
    <AiInsightsModal
      isOpen
      pageName={pageName}
      productType={productType}
      productCode={productCode}
      question={question}
      onClose={() => call.end(true)}
    />
  ),
);
